import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../DataTable/DataTable';
import ImageUploadField from '../ImageUploadField';
import { fetchBlogs, createBlog, editBlog, removeBlog } from '../../redux/services/blogsService';
import { confirmDelete } from '../../utils/sweetalert';
import { hasPermission } from '../../utils/authHelper';
import AccessDenied from '../AccessDenied';
import { getImageUrl } from '../../utils/imageUrl';

const countWords = (text) => (text ? text.trim().split(/\s+/).filter(Boolean).length : 0);

const normalizeLineBreaks = (text) => (typeof text === 'string' ? text.replace(/\r\n/g, '\n').replace(/\r/g, '\n') : text);

const LIMITS = {
    title:    { chars: 150,   words: 25 },
    category: { chars: 60,    words: 6  },
    author:   { chars: 60,    words: 6  },
    excerpt:  { chars: 350,   words: 60 },
    content:  { chars: 50000, words: 5000 },
    tags:     { chars: 250,   words: 30 },
};

const validateField = (field, value) => {
    const limits = LIMITS[field];
    if (!limits) return null;
    const cleanValue = normalizeLineBreaks(value);
    if (cleanValue && cleanValue.length > limits.chars) {
        return `Max ${limits.chars} characters (you have ${cleanValue.length})`;
    }
    if (cleanValue && countWords(cleanValue) > limits.words) {
        return `Max ${limits.words} words (you have ${countWords(cleanValue)})`;
    }
    return null;
};

// Blog Cover Image Dimension Validator (1200x750 - 16:10 landscape)
const BLOG_IMAGE_DIMENSIONS = {
    width: 1200,
    height: 750,
    aspectRatio: 16 / 10,
    tolerance: 0.08,
    maxFileSize: 5 * 1024 * 1024,
    formats: [
        'image/jpeg', 'image/png', 'image/webp', 'image/gif',
        'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon',
        'image/heic', 'image/heif', 'image/avif'
    ],
};

const validateBlogImageDimensions = (file) => {
    return new Promise((resolve) => {
        if (!file) {
            resolve({ valid: true });
            return;
        }
        if (file.size > BLOG_IMAGE_DIMENSIONS.maxFileSize) {
            resolve({ valid: false, error: `File too large. Max: 5MB. You have: ${(file.size / 1024 / 1024).toFixed(2)}MB` });
            return;
        }
        if (!BLOG_IMAGE_DIMENSIONS.formats.includes(file.type)) {
            resolve({ valid: false, error: `Invalid format. Use common image formats (JPG, PNG, WebP, GIF, SVG, BMP, TIFF, ICO, HEIC, HEIF, AVIF). You uploaded: ${file.type || 'unknown'}` });
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const { width, height } = img;
                if (!height || height === 0) {
                    resolve({ valid: false, error: 'Invalid image dimensions.' });
                    return;
                }
                const actualRatio = width / height;
                const expectedRatio = BLOG_IMAGE_DIMENSIONS.aspectRatio;
                const ratioDiff = Math.abs(actualRatio - expectedRatio) / expectedRatio;

                // if (ratioDiff > BLOG_IMAGE_DIMENSIONS.tolerance) {
                //     resolve({
                //         valid: false,
                //         error: `Incorrect aspect ratio. Use 16:10 (${BLOG_IMAGE_DIMENSIONS.width}×${BLOG_IMAGE_DIMENSIONS.height}px). Yours: ${width}×${height}px`,
                //         dimensions: { width, height },
                //     });
                //     return;
                // }

                resolve({
                    valid: true,
                    dimensions: { width, height },
                });
            };
            img.onerror = () => {
                resolve({ valid: false, error: 'Failed to load image file.' });
            };
            img.src = e.target.result;
        };
        reader.onerror = () => {
            resolve({ valid: false, error: 'Failed to read image file.' });
        };
        reader.readAsDataURL(file);
    });
};

// Helper to recursively unwrap / parse tags into a clean array of strings
const parseTagsToArray = (tags) => {
    if (!tags) return [];
    let current = tags;

    // If it's a string, unwrap JSON iteratively if needed
    if (typeof current === 'string') {
        let trimmed = current.trim();
        while (typeof trimmed === 'string' && (trimmed.startsWith('[') || trimmed.startsWith('"') || trimmed.startsWith("'"))) {
            try {
                const parsed = JSON.parse(trimmed);
                if (parsed === trimmed) break;
                current = parsed;
                if (typeof current === 'string') {
                    trimmed = current.trim();
                } else {
                    break;
                }
            } catch (_) {
                break;
            }
        }
    }

    if (Array.isArray(current)) {
        const flat = [];
        const extract = (arr) => {
            arr.forEach(item => {
                if (Array.isArray(item)) {
                    extract(item);
                } else if (typeof item === 'string') {
                    let str = item.trim();
                    while (typeof str === 'string' && str.startsWith('[') && str.endsWith(']')) {
                        try {
                            const inner = JSON.parse(str);
                            if (Array.isArray(inner)) {
                                extract(inner);
                                return;
                            }
                        } catch (_) {
                            break;
                        }
                    }
                    str = str.replace(/^[\[\]"'\\]+|[\[\]"'\\]+$/g, '').trim();
                    if (str) flat.push(str);
                } else if (item !== null && item !== undefined) {
                    flat.push(String(item));
                }
            });
        };
        extract(current);
        return flat;
    }

    if (typeof current === 'string') {
        return current
            .replace(/[\[\]"'\\]/g, ' ')
            .split(/[\s,]+/)
            .map(s => s.trim())
            .filter(Boolean);
    }

    return [];
};

const parseTagsToString = (tags) => {
    const arr = parseTagsToArray(tags);
    return arr.join(', ');
};

export default function Blogs({ showToast }) {
    const dispatch = useDispatch();
    const { items: blogs, loading } = useSelector(state => state.blogs || { items: [], loading: false });

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [previewImg, setPreviewImg] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef();

    const [form, setForm] = useState({
        title: '', slug: '', category: '', excerpt: '', content: '',
        author: 'Healthy Home', date: '', tags: '', isPublished: true, imageFile: null,
        slugModified: false,
    });

    useEffect(() => { dispatch(fetchBlogs()); }, [dispatch]);

    useEffect(() => {
        if (form.imageFile) {
            const url = URL.createObjectURL(form.imageFile);
            setPreviewImg(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [form.imageFile]);

    if (!hasPermission('blogs_view')) return <AccessDenied moduleName="Blogs" />;

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setPreviewImg(null);
        setImageDimensions(null);
        setErrors({});
        setForm({
            title: '', slug: '', category: '', excerpt: '', content: '',
            author: 'Healthy Home', date: '', tags: '', isPublished: true, imageFile: null,
            slugModified: false,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const STOP_WORDS = new Set([
        'a', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 'any', 'are', 'as', 'at',
        'be', 'because', 'been', 'before', 'being', 'below', 'between', 'both', 'but', 'by',
        'for', 'from', 'further', 'had', 'has', 'have', 'having', 'he', 'her', 'here', 'hers', 'him', 'his', 'how',
        'i', 'if', 'in', 'into', 'is', 'it', 'its', 'just', 'me', 'more', 'most', 'my', 'no', 'nor', 'not', 'now', 'of', 'off', 'on', 'or', 'other', 'our', 'out', 'over', 'own',
        'so', 'some', 'such', 'than', 'that', 'the', 'their', 'them', 'then', 'there', 'these', 'they', 'this', 'those', 'through', 'to', 'too',
        'under', 'until', 'up', 'very', 'was', 'we', 'were', 'what', 'when', 'where', 'which', 'while', 'who', 'whom', 'why', 'with', 'you', 'your'
    ]);

    const autoSlug = (title) => {
        if (!title || typeof title !== 'string') return '';
        const words = title
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .filter(Boolean);

        if (words.length === 0) return '';
        let meaningful = words;
        if (words.length > 5) {
            const filtered = words.filter(w => !STOP_WORDS.has(w));
            if (filtered.length >= 3) meaningful = filtered;
        }
        meaningful = meaningful.slice(0, 6);
        let slug = meaningful.join('-');
        if (slug.length > 50) {
            slug = slug.slice(0, 50).replace(/-[^-]*$/, '');
        }
        return slug.replace(/(^-|-$)/g, '');
    };

    const handleEditClick = (row) => {
        setEditingId(row.id);
        const existingSlug = (row.slug || '').trim();
        // If existing slug was corrupted/overly long (> 50 chars), automatically clean it
        const cleanSlug = existingSlug && existingSlug.length <= 50 ? existingSlug : autoSlug(row.title || existingSlug);
        setForm({
            title: row.title || '',
            slug: cleanSlug,
            category: row.category || '',
            excerpt: normalizeLineBreaks(row.excerpt || ''),
            content: normalizeLineBreaks(row.content || ''),
            author: row.author || 'Healthy Home',
            date: row.date ? row.date.split('T')[0] : '',
            tags: parseTagsToString(row.tags),
            isPublished: row.isPublished ?? true,
            imageFile: null,
            slugModified: Boolean(existingSlug && existingSlug.length <= 50 && existingSlug === cleanSlug),
        });
        setPreviewImg(row.image ? getImageUrl(row.image) : null);
        setImageDimensions(null);
        setErrors({});
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFieldChange = (field, value) => {
        const cleanValue = normalizeLineBreaks(value);
        setForm(prev => {
            const updated = { ...prev, [field]: cleanValue };
            if (field === 'title' && !prev.slugModified) {
                updated.slug = autoSlug(cleanValue);
            }
            return updated;
        });
        const err = validateField(field, cleanValue);
        setErrors(prev => ({ ...prev, [field]: err || undefined }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setForm(prev => ({ ...prev, imageFile: file }));
        validateBlogImageDimensions(file).then((result) => {
            setImageDimensions(result);
            if (!result.valid) {
                setErrors(prev => ({ ...prev, image: result.error }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.image;
                    return newErrors;
                });
            }
        });
    };

    const handleClearImage = () => {
        setForm(prev => ({ ...prev, imageFile: null }));
        setPreviewImg(null);
        setImageDimensions(null);
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.image;
            return newErrors;
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        if (!form.title.trim()) {
            newErrors.title = 'Title is required';
        } else {
            const err = validateField('title', form.title);
            if (err) newErrors.title = err;
        }

        if (form.category) {
            const err = validateField('category', form.category);
            if (err) newErrors.category = err;
        }

        if (form.author) {
            const err = validateField('author', form.author);
            if (err) newErrors.author = err;
        }

        if (form.excerpt) {
            const err = validateField('excerpt', form.excerpt);
            if (err) newErrors.excerpt = err;
        }

        if (form.content) {
            const err = validateField('content', form.content);
            if (err) newErrors.content = err;
        }

        if (form.tags) {
            const err = validateField('tags', form.tags);
            if (err) newErrors.tags = err;
        }

        if (!editingId && !form.imageFile && !previewImg) {
            newErrors.image = 'Cover image is required';
        }

        // if (form.imageFile && imageDimensions && !imageDimensions.valid) {
        //     newErrors.image = imageDimensions.error;
        // }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showToast.error(Object.values(newErrors)[0]);
            return;
        }
        setErrors({});

        const fd = new FormData();
        fd.append('title', form.title.trim());
        fd.append('slug', autoSlug(form.slug || form.title));
        fd.append('category', form.category.trim());
        fd.append('excerpt', normalizeLineBreaks(form.excerpt).trim());
        fd.append('content', normalizeLineBreaks(form.content).trim());
        fd.append('author', form.author.trim() || 'Healthy Home');
        fd.append('date', form.date || new Date().toISOString().split('T')[0]);
        fd.append('isPublished', form.isPublished ? 'true' : 'false');
        const tagsArr = parseTagsToArray(form.tags);
        fd.append('tags', JSON.stringify(tagsArr));
        if (form.imageFile) fd.append('image', form.imageFile);

        const toastId = showToast.loading(editingId ? 'Updating blog...' : 'Creating blog...');
        try {
            if (editingId) {
                await dispatch(editBlog({ id: editingId, formData: fd }));
                showToast.success('Blog updated!', toastId);
            } else {
                await dispatch(createBlog(fd));
                showToast.success('Blog created!', toastId);
            }
            dispatch(fetchBlogs());
            resetForm();
        } catch (err) {
            showToast.error(err?.response?.data?.message || err?.message || 'Operation failed', toastId);
        }
    };

    const handleDelete = (id) => {
        confirmDelete({
            title: 'Delete Blog?',
            message: 'This cannot be undone.',
            onConfirm: async () => {
                try { await dispatch(removeBlog(id)); }
                catch (err) { console.error(err); }
            },
        });
    };

    const rows = Array.isArray(blogs) ? blogs : [];

    return (
        <div className="categories-container">
            <div className="section-header">
                <div className="section-title">Blog Posts</div>
                {hasPermission('blogs_create') && (
                    <button className="action-btn btn-edit" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}>
                        {showForm ? 'Close' : '+ Add Blog'}
                    </button>
                )}
            </div>

            {showForm && (
                <div className="km-form-card fade-in">
                    <div className="km-form-header">
                        <div className="km-form-header-icon">{editingId ? '✎' : '📝'}</div>
                        <div>
                            <div className="km-form-header-title">{editingId ? 'Edit Blog Post' : 'Create New Blog Post'}</div>
                            <div className="km-form-header-sub">Fill in the blog details below</div>
                        </div>
                    </div>

                    <div className="km-form-body">
                        <form className="km-form-grid" onSubmit={handleSubmit}>

                            {/* Title */}
                            <div className="km-field">
                                <label className="km-label">
                                    Title <span style={{ color: '#ef4444' }}>*</span>
                                    <span className={`km-char-count${errors.title ? ' km-char-count-err' : ''}`}>
                                        {form.title.length}/{LIMITS.title.chars} chars · {countWords(form.title)}/{LIMITS.title.words} words
                                    </span>
                                </label>
                                <input
                                    className={`km-input${errors.title ? ' km-input-error' : ''}`}
                                    value={form.title}
                                    maxLength={LIMITS.title.chars}
                                    onChange={e => handleFieldChange('title', e.target.value)}
                                    placeholder="e.g. Health Benefits of Wood Pressed Oils"
                                    required
                                />
                                {errors.title && <div className="km-error-msg">⚠ {errors.title}</div>}
                            </div>

                            {/* Slug */}
                            <div className="km-field">
                                <label className="km-label">Slug (URL)</label>
                                <input
                                    className="km-input"
                                    value={form.slug}
                                    maxLength={80}
                                    onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/(^-|-$)/g, ''), slugModified: true }))}
                                    placeholder="auto-generated from title"
                                />
                            </div>

                            {/* Category */}
                            <div className="km-field">
                                <label className="km-label">
                                    Category
                                    <span className={`km-char-count${errors.category ? ' km-char-count-err' : ''}`}>
                                        {form.category.length}/{LIMITS.category.chars} chars · {countWords(form.category)}/{LIMITS.category.words} words
                                    </span>
                                </label>
                                <input
                                    className={`km-input${errors.category ? ' km-input-error' : ''}`}
                                    value={form.category}
                                    maxLength={LIMITS.category.chars}
                                    onChange={e => handleFieldChange('category', e.target.value)}
                                    placeholder="e.g. Cold Pressed Oils / Natural Sweeteners"
                                />
                                {errors.category && <div className="km-error-msg">⚠ {errors.category}</div>}
                            </div>

                            {/* Author */}
                            <div className="km-field">
                                <label className="km-label">
                                    Author
                                    <span className={`km-char-count${errors.author ? ' km-char-count-err' : ''}`}>
                                        {form.author.length}/{LIMITS.author.chars} chars · {countWords(form.author)}/{LIMITS.author.words} words
                                    </span>
                                </label>
                                <input
                                    className={`km-input${errors.author ? ' km-input-error' : ''}`}
                                    value={form.author}
                                    maxLength={LIMITS.author.chars}
                                    onChange={e => handleFieldChange('author', e.target.value)}
                                    placeholder="e.g. Healthy Home"
                                />
                                {errors.author && <div className="km-error-msg">⚠ {errors.author}</div>}
                            </div>

                            {/* Date */}
                            <div className="km-field">
                                <label className="km-label">Published Date</label>
                                <input type="date" className="km-input" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                            </div>

                            {/* Status */}
                            <div className="km-field">
                                <label className="km-label">Status</label>
                                <select className="km-input" value={form.isPublished ? 'true' : 'false'} onChange={e => setForm(p => ({ ...p, isPublished: e.target.value === 'true' }))}>
                                    <option value="true">Published</option>
                                    <option value="false">Draft</option>
                                </select>
                            </div>

                            {/* Tags */}
                            <div className="km-field km-field-full">
                                <label className="km-label">
                                    Tags (comma separated)
                                    <span className={`km-char-count${errors.tags ? ' km-char-count-err' : ''}`}>
                                        {form.tags.length}/{LIMITS.tags.chars} chars · {countWords(form.tags)}/{LIMITS.tags.words} words
                                    </span>
                                </label>
                                <input
                                    className={`km-input${errors.tags ? ' km-input-error' : ''}`}
                                    value={form.tags}
                                    maxLength={LIMITS.tags.chars}
                                    onChange={e => handleFieldChange('tags', e.target.value)}
                                    placeholder="e.g. cold pressed oils, pink salt, jaggery, A2 ghee, wellness"
                                />
                                {errors.tags && <div className="km-error-msg">⚠ {errors.tags}</div>}
                            </div>

                            {/* Excerpt */}
                            <div className="km-field km-field-full">
                                <label className="km-label">
                                    Short Description (Excerpt)
                                    <span className={`km-char-count${errors.excerpt ? ' km-char-count-err' : ''}`}>
                                        {form.excerpt.length}/{LIMITS.excerpt.chars} chars · {countWords(form.excerpt)}/{LIMITS.excerpt.words} words
                                    </span>
                                </label>
                                <textarea
                                    className={`km-input${errors.excerpt ? ' km-input-error' : ''}`}
                                    rows={3}
                                    value={form.excerpt}
                                    maxLength={LIMITS.excerpt.chars}
                                    onChange={e => handleFieldChange('excerpt', e.target.value)}
                                    placeholder="A brief description shown on blog card..."
                                    style={{ resize: 'vertical', minHeight: 80 }}
                                />
                                {errors.excerpt && <div className="km-error-msg">⚠ {errors.excerpt}</div>}
                            </div>

                            {/* Content */}
                            <div className="km-field km-field-full">
                                <label className="km-label">
                                    Full Content
                                    <span className={`km-char-count${errors.content ? ' km-char-count-err' : ''}`}>
                                        {form.content.length}/{LIMITS.content.chars} chars · {countWords(form.content)}/{LIMITS.content.words} words
                                    </span>
                                </label>
                                <textarea
                                    className={`km-input${errors.content ? ' km-input-error' : ''}`}
                                    rows={8}
                                    value={form.content}
                                    maxLength={LIMITS.content.chars}
                                    onChange={e => handleFieldChange('content', e.target.value)}
                                    placeholder="Full blog article content..."
                                    style={{ resize: 'vertical', minHeight: 180 }}
                                />
                                {errors.content && <div className="km-error-msg">⚠ {errors.content}</div>}
                            </div>

                            {/* Image upload */}
                            <ImageUploadField
                                label="Cover Image"
                                imageFile={form.imageFile}
                                preview={previewImg}
                                fileInputRef={fileInputRef}
                                onFileChange={handleImageChange}
                                onClear={handleClearImage}
                                validation={imageDimensions}
                                error={errors.image}
                                requirements={`${BLOG_IMAGE_DIMENSIONS.width}×${BLOG_IMAGE_DIMENSIONS.height}px (16:10) • Max: 5MB`}
                            />

                            <div className="km-form-actions" style={{ display: 'flex' }}>
                                <button type="submit" className="km-btn-submit">{editingId ? 'Update Blog' : 'Create Blog'}</button>
                                <button type="button" className="km-btn-cancel" onClick={resetForm}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="km-loading">Loading blogs...</p>
            ) : (
                <DataTable
                    columns={(() => {
                        const cols = ['No.', 'Cover', 'Title', 'Category', 'Author', 'Status'];
                        if (hasPermission('blogs_edit') || hasPermission('blogs_delete')) cols.push('Actions');
                        return cols;
                    })()}
                    initialRows={rows}
                    renderRow={(row, i) => (
                        <tr key={row.id}>
                            <td className="td-id">{i + 1}</td>
                            <td>
                                {row.image
                                    ? <img src={getImageUrl(row.image)} alt="" style={{ width: 60, height: 42, objectFit: 'cover', borderRadius: 6 }} />
                                    : <span style={{ color: '#9ca3af', fontSize: 12 }}>No Image</span>
                                }
                            </td>
                            <td style={{ maxWidth: 250, fontSize: 13 }}>
                                <div style={{ fontWeight: 600 }}>{row.title}</div>
                                <div style={{ color: '#6b7280', fontSize: 11 }}>{row.slug}</div>
                            </td>
                            <td style={{ fontSize: 13 }}>{row.category || '—'}</td>
                            <td style={{ fontSize: 13 }}>{row.author || '—'}</td>
                            <td>
                                <span className={`status-pill ${row.isPublished ? 'pill-active' : 'pill-inactive'}`}>
                                    {row.isPublished ? 'Published' : 'Draft'}
                                </span>
                            </td>
                            {(hasPermission('blogs_edit') || hasPermission('blogs_delete')) && (
                                <td>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {hasPermission('blogs_edit') && (
                                            <button className="action-btn btn-edit" onClick={() => handleEditClick(row)}>Edit</button>
                                        )}
                                        {hasPermission('blogs_delete') && (
                                            <button className="action-btn btn-del" onClick={() => handleDelete(row.id)}>Delete</button>
                                        )}
                                    </div>
                                </td>
                            )}
                        </tr>
                    )}
                />
            )}

            <style>{`
        .km-char-count {
          font-size: 11px;
          font-weight: 400;
          color: #9ca3af;
          margin-left: 8px;
        }
        .km-char-count-err {
          color: #ef4444;
          font-weight: 600;
        }
        .km-error-msg {
          color: #ef4444;
          font-size: 12px;
          margin-top: 6px;
          font-weight: 500;
        }
        .km-input-error {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 2px rgba(239,68,68,0.1) !important;
        }
        .status-pill { font-size: 12px; font-weight: 600; border-radius: 6px; padding: 3px 10px; display: inline-block; }
        .pill-active  { background: rgba(69,179,105,0.15); color: #45b369; }
        .pill-inactive{ background: rgba(239,68,68,0.15);  color: #ef4444; }
        .km-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .km-field-full { grid-column: span 2; }
        @media (max-width: 600px) {
          .km-form-grid { grid-template-columns: 1fr; }
          .km-field-full { grid-column: span 1; }
        }
        .km-form-actions {
          grid-column: span 2;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 10px;
          padding-top: 20px;
          border-top: 1px solid rgba(0,0,0,0.05);
        }
        .km-btn-submit {
          padding: 10px 24px;
          background: #299d5a;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .km-btn-cancel {
          padding: 10px 24px;
          background: #f1f1f1;
          color: #333;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        .km-btn-submit:hover, .km-btn-cancel:hover {
          opacity: 0.8;
        }
        .fade-in {
          animation: kmFadeIn 0.3s ease-out;
        }
        @keyframes kmFadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
}
